'use client';

import { LinkIcon } from '@chakra-ui/icons';
import { useToast } from '@chakra-ui/react';
import { useState } from 'react';

import { generateShortUrl } from '@/services/short-url.service';

import styles from './ShortUrlButton.module.scss';

interface ShortUrlButtonProps {
  isAuthorized: boolean;
  dealershipId: string | null;
  salespersonEmail: string | null;
}

export default function ShortUrlButton({
  isAuthorized,
  dealershipId,
  salespersonEmail,
}: ShortUrlButtonProps) {
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleGenerate = async () => {
    if (!isAuthorized) return;

    setLoading(true);
    try {
      const pageUrl = new URL(window.location.href);
      const params = new URLSearchParams(pageUrl.search);

      if (!params.has('utm_source')) params.set('utm_source', 'social_share');
      if (!params.has('utm_medium')) params.set('utm_medium', 'social');
      if (dealershipId) params.set('utm_dealership', dealershipId);
      if (salespersonEmail) params.set('utm_salesperson', salespersonEmail);

      pageUrl.search = params.toString();

      const result = await generateShortUrl(pageUrl.toString());

      if (result.success && result.short_url) {
        setShortUrl(result.short_url);
        await navigator.clipboard.writeText(result.short_url);

        toast({
          title: 'Short URL copied!',
          description: 'The short link has been copied to your clipboard.',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Failed to generate short URL',
          description: 'Please try again later.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error generating short URL:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) return null;

  return (
    <div className={styles.shortUrlButton}>
      <button
        className={styles.button}
        onClick={loading ? undefined : handleGenerate}
        title="Generate short URL"
      >
        {loading ? (
          <span className={styles.loadingSpinner} />
        ) : (
          <LinkIcon fontWeight="bold" />
        )}
      </button>

      {shortUrl && (
        <div className={styles.popup}>
          <div className={styles.urlContainer}>
            <a
              href={shortUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.shortLink}
            >
              {shortUrl}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
